import React, { useContext, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  HStack,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast
} from '@chakra-ui/react'
import { startCase } from 'lodash'
import { renewals } from '@homevest/utils'

import SignSection from 'components/RentRoll/Details/Agreements/SignSection'
import { DocumentDownloadButton } from 'components/Documents/DownloadDocumentButton'
import { formatUTCDate } from 'lib/date-time'
import { formatMoney } from 'lib/numbers'
import { formatRenewalStatus } from 'lib/renewals'
import { Renewal } from 'types/Renewals'
import { RentalDetailsContext } from '../RentalDetailsContext'
import { groupRenewalsForDisplay, summarizeSignerProgress } from './renewalPresentation'

const { RENEWAL_STATUSES } = renewals

const renewalStatusColor = (status: string) => {
  if (status === RENEWAL_STATUSES.COMPLETE) return 'green'
  if (status === RENEWAL_STATUSES.CANCELED) return 'red'
  return 'orange'
}

const formatChargeStatus = (status: string) =>
  status === 'contingent'
    ? 'Waiting for the renewal to be fully signed'
    : formatRenewalStatus(status)

const DetailSection = ({ children, title }: React.PropsWithChildren<{ title: string }>) => (
  <Box>
    <Text fontSize='sm' fontWeight='semibold' mb={2}>
      {title}
    </Text>
    <Stack spacing={2}>{children}</Stack>
  </Box>
)

const Detail = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box>
    <Text color='gray.500' fontSize='xs'>
      {label}
    </Text>
    <Box fontSize='sm'>{value}</Box>
  </Box>
)

const moneyOrDash = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—'
  const amount = Number(typeof value === 'string' ? value.replace(/[$,\s]/g, '') : value)
  return Number.isFinite(amount) ? formatMoney(amount, 2, '$') : '—'
}

const RenewalsTable = ({ renewals: renewalList }: { renewals: Renewal[] }) => {
  const toast = useToast()
  const { refreshRentalDetails } = useContext(RentalDetailsContext)
  const [showCanceled, setShowCanceled] = useState(false)
  const [expandedRenewalIds, setExpandedRenewalIds] = useState<string[]>([])
  const { current, canceled } = groupRenewalsForDisplay(renewalList)

  const onSign = async () => {
    toast({
      title: 'Renewal signed',
      description: 'The renewal has been signed successfully.',
      status: 'success',
      duration: 5000,
      isClosable: true,
      position: 'top-right'
    })
    refreshRentalDetails()
  }

  const toggleDetails = (renewalId: string) => {
    setExpandedRenewalIds((currentIds) =>
      currentIds.includes(renewalId)
        ? currentIds.filter((id) => id !== renewalId)
        : [...currentIds, renewalId]
    )
  }

  const renderRenewalRows = (rows: Renewal[]) =>
    rows.map((renewal) => {
      const customFields = renewal.agreement?.metadata?.custom_fields ?? {}
      const startDate = customFields.newLeaseStartDate ?? customFields.startDate
      const endDate = customFields.newLeaseEndDate ?? customFields.endDate
      const rent =
        renewal.selected_renewal_offer?.price ?? customFields.newLeaseRent ?? customFields.rent
      const formattedRent = moneyOrDash(rent)
      const rawTerm = renewal.selected_renewal_offer?.term ?? customFields.extensionMonthsInDigits
      const term = Number.isFinite(Number(rawTerm)) ? Number(rawTerm) : null
      const kcpoaTerms = (renewal.kcpoa_terms ?? {}) as Record<string, number>
      const schedule = renewal.scheduled_rental_liabilities[0]
      const signers = renewal.agreement?.agreement_signers ?? []
      const isCanceled = renewal.status === RENEWAL_STATUSES.CANCELED
      const isExpanded = expandedRenewalIds.includes(renewal.id)

      return (
        <React.Fragment key={renewal.id}>
          <Tr verticalAlign='top'>
            <Td py={4} width='25%' whiteSpace='normal'>
              <Stack align='start' spacing={1}>
                <Badge
                  colorScheme={renewalStatusColor(renewal.status)}
                  lineHeight='shorter'
                  maxW='100%'
                  whiteSpace='normal'
                >
                  {formatRenewalStatus(renewal.status)}
                </Badge>
                <Text fontSize='sm' fontWeight='semibold'>
                  {renewal.lease_variant === 'kcpoa' ? 'RPP' : 'Standard'} renewal
                </Text>
                <Text color='gray.500' fontSize='xs'>
                  {renewal.offered_at
                    ? `Issued ${new Date(renewal.offered_at).toLocaleDateString()} by ${
                        renewal.admin.first_name
                      } ${renewal.admin.last_name}`
                    : 'Not issued'}
                </Text>
              </Stack>
            </Td>
            <Td py={4} width='25%' whiteSpace='normal'>
              <Text fontSize='sm' fontWeight='medium'>
                {startDate && endDate
                  ? `${formatUTCDate(startDate, 'MMM d, yyyy')} – ${formatUTCDate(
                      endDate,
                      'MMM d, yyyy'
                    )}`
                  : 'Dates not recorded'}
              </Text>
              <Text fontSize='sm' mt={1}>
                {formattedRent === '—' ? 'Rent not recorded' : `${formattedRent}/month`}
              </Text>
              <Text color='gray.500' fontSize='xs'>
                {term ? `${term} ${term === 1 ? 'month' : 'months'}` : 'Term not recorded'}
              </Text>
            </Td>
            <Td py={4} width='30%' whiteSpace='normal'>
              {isCanceled ? (
                <Stack spacing={1}>
                  <Text fontSize='sm' fontWeight='medium'>
                    Canceled{' '}
                    {renewal.canceled_at ? new Date(renewal.canceled_at).toLocaleDateString() : ''}
                  </Text>
                  {renewal.cancelation_reason && (
                    <Text color='gray.600' fontSize='sm'>
                      {startCase(renewal.cancelation_reason)}
                    </Text>
                  )}
                </Stack>
              ) : (
                <Text fontSize='sm' fontWeight='medium'>
                  {summarizeSignerProgress(signers)}
                </Text>
              )}
            </Td>
            <Td py={4} width='20%' whiteSpace='normal'>
              <VStack align='stretch' spacing={2}>
                {renewal.status === RENEWAL_STATUSES.PENDING_UPANDUP_SIGNATURE && (
                  <SignSection agreementWithSigners={renewal.agreement} onSign={onSign} />
                )}
                {renewal.agreement?.documents?.[0] && (
                  <HStack justify='flex-end'>
                    <Text fontSize='xs'>Executed PDF</Text>
                    <DocumentDownloadButton
                      documentId={renewal.agreement.documents[0].id as string}
                    />
                  </HStack>
                )}
                <Button
                  colorScheme='teal'
                  size='sm'
                  variant='ghost'
                  onClick={() => toggleDetails(renewal.id)}
                >
                  {isExpanded ? 'Hide details' : 'View details'}
                </Button>
              </VStack>
            </Td>
          </Tr>
          {isExpanded && (
            <Tr>
              <Td colSpan={4} bg='gray.50' p={5} whiteSpace='normal'>
                <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={6}>
                  <DetailSection title='Issuance'>
                    <Detail
                      label='Request ID'
                      value={
                        <Text wordBreak='break-all'>
                          {renewal.agreement?.external_id || 'Not available'}
                        </Text>
                      }
                    />
                    <Detail
                      label='Issued'
                      value={
                        renewal.offered_at
                          ? new Date(renewal.offered_at).toLocaleString()
                          : 'Not issued'
                      }
                    />
                    {renewal.canceled_at && (
                      <Detail
                        label='Canceled'
                        value={`${new Date(renewal.canceled_at).toLocaleString()}${
                          renewal.cancelation_reason
                            ? ` · ${startCase(renewal.cancelation_reason)}`
                            : ''
                        }`}
                      />
                    )}
                  </DetailSection>

                  <DetailSection title='Signers'>
                    {signers.length ? (
                      signers.map((signer) => (
                        <Detail
                          key={signer.id}
                          label={startCase(signer.role)}
                          value={`${formatRenewalStatus(signer.status)}${
                            signer.signed_at
                              ? ` · ${new Date(signer.signed_at).toLocaleString()}`
                              : ''
                          }`}
                        />
                      ))
                    ) : (
                      <Text color='gray.500' fontSize='sm'>
                        No signer records
                      </Text>
                    )}
                  </DetailSection>

                  {schedule && (
                    <DetailSection title='Scheduled charge'>
                      <Detail
                        label='Amount and due date'
                        value={`${moneyOrDash(schedule.amount)} due ${formatUTCDate(
                          schedule.due_date,
                          'MMM d, yyyy'
                        )}`}
                      />
                      <Detail label='Status' value={formatChargeStatus(schedule.status)} />
                      {schedule.last_error && (
                        <Text color='red.600' fontSize='sm'>
                          {schedule.last_error}
                        </Text>
                      )}
                    </DetailSection>
                  )}

                  <DetailSection
                    title={
                      renewal.lease_variant === 'kcpoa' ? 'Purchase-option terms' : 'Lease terms'
                    }
                  >
                    <Detail label='Monthly rent' value={moneyOrDash(rent)} />
                    {renewal.lease_variant === 'kcpoa' ? (
                      <>
                        <Detail
                          label='Monthly option fee'
                          value={moneyOrDash(kcpoaTerms.monthlyOptionFee)}
                        />
                        <Detail
                          label='Initial option fee'
                          value={moneyOrDash(kcpoaTerms.initialOptionFee)}
                        />
                        <Detail
                          label='Deposit credit'
                          value={moneyOrDash(kcpoaTerms.depositCredit)}
                        />
                        <Detail
                          label='Purchase price'
                          value={moneyOrDash(kcpoaTerms.purchasePrice)}
                        />
                      </>
                    ) : (
                      <Detail label='Term' value={term ? `${term} months` : '—'} />
                    )}
                  </DetailSection>
                </SimpleGrid>
              </Td>
            </Tr>
          )}
        </React.Fragment>
      )
    })

  const renewalTable = (rows: Renewal[]) => (
    <TableContainer whiteSpace='normal'>
      <Table size='sm' minW='760px' style={{ tableLayout: 'fixed' }}>
        <Thead bg='gray.50'>
          <Tr>
            <Th>Renewal</Th>
            <Th>Terms</Th>
            <Th>Progress</Th>
            <Th textAlign='right'>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>{renderRenewalRows(rows)}</Tbody>
      </Table>
    </TableContainer>
  )

  return (
    <Stack spacing={4}>
      {current.length ? (
        <Box borderColor='gray.200' borderRadius='lg' borderWidth='1px' overflow='hidden'>
          {renewalTable(current)}
        </Box>
      ) : (
        <Box bg='gray.50' borderRadius='md' px={4} py={3}>
          <Text color='gray.600' fontSize='sm'>
            No current or completed renewals.
          </Text>
        </Box>
      )}

      {canceled.length > 0 && (
        <Box borderColor='gray.200' borderRadius='lg' borderWidth='1px' overflow='hidden'>
          <Button
            borderRadius={0}
            h='auto'
            justifyContent='space-between'
            p={4}
            variant='ghost'
            width='full'
            onClick={() => setShowCanceled((isVisible) => !isVisible)}
          >
            <Box textAlign='left'>
              <Text fontWeight='semibold'>Canceled renewal history</Text>
              <Text color='gray.500' fontSize='sm' fontWeight='normal'>
                {canceled.length} canceled {canceled.length === 1 ? 'renewal' : 'renewals'} hidden
                from the main view
              </Text>
            </Box>
            <Text color='teal.600'>{showCanceled ? 'Hide' : `Show ${canceled.length}`}</Text>
          </Button>
          <Collapse in={showCanceled} animateOpacity>
            <Divider />
            {renewalTable(canceled)}
          </Collapse>
        </Box>
      )}
    </Stack>
  )
}

export default RenewalsTable
